import {
  KNOWLEDGE_UPDATE_CATEGORY,
  KNOWLEDGE_DELETE_CATEGORY,
  KNOWLEDGE_CREATE_CATEGORY,
  KNOWLEDGE_UPDATE_SUBCATEGORIES,
  KNOWLEDGE_UPDATE_CATEGORY_LOCATION,
} from '.';
import knowledgeServiceApi from '../../api/knowledge.service.api';
import analyticsService from 'src/helpers/analytics.service';
import { EVENTS } from '../../constants';
import { getChangedFields } from '../../utils';
import { ENTITY_TYPES } from '../../constants';

export const updateCategory =
  ({ id, title, subtitle, ownerIds, path, tags, options, ...object }) =>
  async (dispatch, getState) => {
    const initalCategory = getState().knowledge.categoriesById[id];
    let change = { id, ...object };

    if (title) change.title = title;
    if (subtitle) change.subtitle = subtitle;
    if (ownerIds) change.ownerIds = ownerIds;
    if (path) change.path = path;
    if (tags) change.tags = tags;
    if (options) change.options = options;

    let res = await knowledgeServiceApi.updateCategory(change);

    if (res.error) {
      return { error: res.data.message };
    }

    const changes = getChangedFields(initalCategory, change);
    if (changes.options?.from?.hideInWidget === changes.options?.to?.hideInWidget) {
      delete changes.options;
    }
    analyticsService.track(EVENTS.CATEGORIES.UPDATE, {
      categoryId: id,
      changes: changes,
    });

    // The reducer $merges top-level keys, so a partial `options` would drop stored keys the
    // client never sends — e.g. externalSyncProvider, unlocking a synced category in the UI.
    const storedChange = change.options
      ? { ...change, options: { ...initalCategory?.options, ...change.options } }
      : change;

    dispatch({
      type: KNOWLEDGE_UPDATE_CATEGORY,
      payload: storedChange,
    });

    return { ...storedChange, type: res.data?.type };
  };

export const moveKnowledgeCategory =
  ({ id, categoryId, sourceId }) =>
  async dispatch => {
    let res = await knowledgeServiceApi.moveKnowledgeCategory({
      id,
      categoryId,
      sourceId,
    });

    if (res.error) {
      throw new Error(res.data.message);
    }

    analyticsService.track(EVENTS.CATEGORIES.MOVE, {
      id,
      sourceId,
      targetId: categoryId,
      type: ENTITY_TYPES.knowledge,
    });

    dispatch({
      type: KNOWLEDGE_UPDATE_CATEGORY_LOCATION,
    });
  };

export const mirrorCategory =
  ({ sourceCategoryId, targetCategoryId, ownerIds }) =>
  async dispatch => {
    const res = await knowledgeServiceApi.mirrorCategory({
      sourceCategoryId,
      targetCategoryId,
      ownerIds,
    });

    if (res.error) {
      throw new Error(res.data?.message || 'Failed to mirror category');
    }

    analyticsService.track(EVENTS.CATEGORIES.COPY, {
      sourceCategoryId,
      targetCategoryId,
      type: ENTITY_TYPES.knowledge,
    });

    dispatch({
      type: KNOWLEDGE_UPDATE_CATEGORY_LOCATION,
    });
  };

export const detachMirroredCategory =
  ({ id }) =>
  async dispatch => {
    const res = await knowledgeServiceApi.detachMirroredCategory({ categoryId: id });

    if (res.error) {
      throw new Error(res.data?.message || 'Failed to detach mirrored category');
    }

    dispatch({
      type: KNOWLEDGE_UPDATE_CATEGORY_LOCATION,
    });
  };

export const deleteCategory =
  ({ id }) =>
  async dispatch => {
    let res = await knowledgeServiceApi.deleteCategory({ id });

    if (res.error) {
      // TODO: add monitor
      return false;
    }

    dispatch({
      type: KNOWLEDGE_DELETE_CATEGORY,
      payload: { id },
    });
  };

export const createCategory = data => async (dispatch, getState) => {
  const workspaceId = getState().workspace.selected;

  let res = await knowledgeServiceApi.createCategory({ data, workspaceId });

  if (res.error) {
    return { error: res.data.message };
  }

  dispatch({
    type: KNOWLEDGE_CREATE_CATEGORY,
    payload: {
      category: res.data,
      path: data.path,
    },
  });

  return res.data;
};

export const updateSubcategoriesOrder =
  ({ id, categoryId, index }) =>
  async (dispatch, getState) => {
    const res = await knowledgeServiceApi.updateSubcategoriesOrder({ id, categoryId, index });

    if (res.error) {
      const err = new Error(res.data?.message);
      err.code = res.data?.code;
      throw err;
    }

    dispatch({
      type: KNOWLEDGE_UPDATE_SUBCATEGORIES,
      payload: { id, categoryId, index },
    });

    // Backend clamps user-added index inside mirror parents — refetch to sync UI with the clamped position.
    const parent = getState().knowledge?.categoriesById?.[id];
    if (parent?.mirroredFrom) {
      const refetch = await knowledgeServiceApi.getKnowledgeCategoryById({ id });
      if (!refetch.error) {
        dispatch({
          type: KNOWLEDGE_UPDATE_CATEGORY,
          payload: { id, subcategories: refetch.data.subcategories },
        });
      }
    }
  };
